import { ChevronDown, SearchLg } from '@untitledui/icons';
import type { FC, HTMLAttributes } from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { Selection } from 'react-aria-components';
import {
  Button as AriaButton,
  Dialog as AriaDialog,
  DialogTrigger,
  Header,
  Input as AriaInput,
  ListBox,
  ListBoxItem,
  ListBoxSection,
  SearchField,
} from 'react-aria-components';

import { Popover } from '@/lib/base/overlays/popover';
import { cx } from '@/utils/cx';

import type {
  CardFieldRef,
  FieldPickerOption,
} from '../utils/cardConfigOptions';

// ── Module-level constants (stable references, never recreated) ──────────────

const TRIGGER_CLASS = cx(
  'flex items-center gap-1.5 px-2.5 py-1.5 min-w-0 w-full',
  'rounded-md border border-[var(--color-border-secondary)]',
  'text-sm text-[var(--color-text-secondary)] cursor-pointer',
  'hover:bg-[var(--color-bg-secondary)] transition-colors duration-100',
  'outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]'
);

// Surface, ring, radius and enter/exit animation come from the base Popover;
// these override only its width (fixed, not trigger-width) and padding — the
// panel's inner search row and list own their spacing and scrolling.
const POPOVER_CLASS = 'w-64 min-w-0 overflow-hidden py-0';

const SEARCH_INPUT_CLASS = cx(
  'w-full pl-8 pr-3 py-1.5 text-sm rounded-md',
  'bg-[var(--color-bg-secondary)] text-[var(--color-text-primary)]',
  'placeholder:text-[var(--color-text-tertiary)]',
  'border border-[var(--color-border-secondary)]',
  'outline-none focus:ring-2 focus:ring-[var(--color-focus-ring)] focus:border-transparent'
);

const SECTION_HEADER_CLASS = cx(
  'block px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide',
  'text-[var(--color-text-tertiary)]'
);

const LIST_BOX_ITEM_CLASS = 'px-1.5 py-px outline-none cursor-pointer';

// ── FieldItem — extracted to module scope (no inline component definitions) ──

interface FieldItemProps {
  option: FieldPickerOption;
  isDisabled: boolean;
}

type AnyIconFC = FC<HTMLAttributes<HTMLOrSVGElement>>;

function FieldItem({ option, isDisabled }: FieldItemProps) {
  const Icon = option.icon as AnyIconFC;
  const id = `${option.type}:${option.name}`;
  return (
    <ListBoxItem
      id={id}
      textValue={option.label}
      isDisabled={isDisabled}
      className={LIST_BOX_ITEM_CLASS}
    >
      {({ isFocused, isSelected, isDisabled: itemDisabled }) => (
        <div
          className={cx(
            'flex items-center gap-2 px-2.5 py-2 rounded-md transition-colors duration-100',
            isSelected && 'bg-active',
            isFocused && !isSelected && 'bg-primary_hover',
            itemDisabled && 'opacity-50 cursor-not-allowed'
          )}
        >
          <Icon
            className='size-4 shrink-0 text-[var(--color-text-tertiary)]'
            aria-hidden
          />
          <span className='text-sm font-medium text-[var(--color-text-secondary)] truncate'>
            {option.label}
          </span>
        </div>
      )}
    </ListBoxItem>
  );
}

// ── renderEmptyState — module scope (stable reference for ListBox prop) ──────

function renderEmptyState() {
  return (
    <p className='px-3 py-2 text-sm text-secondary'>
      No fields match your search
    </p>
  );
}

// ── Public interface ─────────────────────────────────────────────────────────

export interface CardFieldPickerProps {
  properties: FieldPickerOption[];
  associations: FieldPickerOption[];
  value?: CardFieldRef;
  /** Disabled options, keyed by `${type}:${name}` (type-aware dedupe). */
  disabledKeys?: Set<string>;
  onSelect: (field: CardFieldRef) => void;
  placeholder?: string;
  triggerAriaLabel: string;
}

export function CardFieldPicker({
  properties,
  associations,
  value,
  disabledKeys,
  onSelect,
  placeholder = 'Select field',
  triggerAriaLabel,
}: CardFieldPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Resolve the selected option for the trigger label/icon
  const selectedOption = useMemo(() => {
    if (!value) {
      return null;
    }
    const list = value.type === 'property' ? properties : associations;
    return list.find(opt => opt.name === value.name) ?? null;
  }, [value, properties, associations]);

  // Filter both groups by case-insensitive label substring
  const filteredProperties = useMemo(() => {
    if (!searchQuery.trim()) {
      return properties;
    }
    const q = searchQuery.toLowerCase();
    return properties.filter(opt => opt.label.toLowerCase().includes(q));
  }, [properties, searchQuery]);

  const filteredAssociations = useMemo(() => {
    if (!searchQuery.trim()) {
      return associations;
    }
    const q = searchQuery.toLowerCase();
    return associations.filter(opt => opt.label.toLowerCase().includes(q));
  }, [associations, searchQuery]);

  // Highlight the currently selected field. Id format matches FieldItem: `${type}:${name}`.
  const selectedKeys = useMemo<Selection>(
    () => (value ? new Set([`${value.type}:${value.name}`]) : new Set()),
    [value]
  );

  // Reset the search query whenever the popover closes (outside click, Escape, or
  // selection) so reopening always shows the full, unfiltered list.
  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSearchQuery('');
    }
  }, []);

  // Parse `${type}:${name}` — split on FIRST colon only so names with `:` survive
  const handleSelectionChange = useCallback(
    (keys: Selection) => {
      const key = [...keys][0];
      if (!key) {
        return;
      }
      const id = String(key);
      const i = id.indexOf(':');
      const type = id.slice(0, i) as CardFieldRef['type'];
      const name = id.slice(i + 1);
      onSelect({ type, name });
      setIsOpen(false);
      setSearchQuery('');
    },
    [onSelect]
  );

  const SelectedIcon = selectedOption
    ? (selectedOption.icon as AnyIconFC)
    : null;

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
      <AriaButton aria-label={triggerAriaLabel} className={TRIGGER_CLASS}>
        {SelectedIcon && (
          <SelectedIcon
            className='size-4 shrink-0 text-[var(--color-text-tertiary)]'
            aria-hidden
          />
        )}
        <span
          className={cx(
            'truncate flex-1 text-left',
            !selectedOption && 'text-[var(--color-text-tertiary)]'
          )}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown
          className='size-4 shrink-0 text-[var(--color-text-tertiary)]'
          aria-hidden
        />
      </AriaButton>

      <Popover
        size='md'
        placement='bottom start'
        offset={8}
        containerPadding={12}
        className={POPOVER_CLASS}
      >
        {/* The Dialog takes the id DialogTrigger stamps into the trigger's
            aria-controls and owns focus + Escape — the non-modal Popover root
            is not a dialog, so without it both would be lost. */}
        <AriaDialog
          aria-label='Choose a field'
          className='flex flex-col outline-hidden'
        >
          {/* Search field */}
          <div className='p-2 border-b border-[var(--color-border-secondary)]'>
            <SearchField
              value={searchQuery}
              onChange={setSearchQuery}
              aria-label='Search fields'
              className='w-full'
            >
              <div className='relative'>
                <SearchLg
                  className='absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-[var(--color-text-tertiary)] pointer-events-none'
                  aria-hidden
                />
                <AriaInput
                  autoFocus
                  placeholder='Search'
                  className={SEARCH_INPUT_CLASS}
                />
              </div>
            </SearchField>
          </div>

          {/* Grouped list */}
          <div className='overflow-y-auto max-h-52 py-1'>
            <ListBox
              aria-label='Available fields'
              selectionMode='single'
              selectedKeys={selectedKeys}
              onSelectionChange={handleSelectionChange}
              className='outline-none'
              renderEmptyState={renderEmptyState}
            >
              {filteredProperties.length > 0 && (
                <ListBoxSection>
                  <Header className={SECTION_HEADER_CLASS}>Properties</Header>
                  {filteredProperties.map(opt => (
                    <FieldItem
                      key={`property:${opt.name}`}
                      option={opt}
                      isDisabled={
                        disabledKeys?.has(`${opt.type}:${opt.name}`) ?? false
                      }
                    />
                  ))}
                </ListBoxSection>
              )}
              {filteredAssociations.length > 0 && (
                <ListBoxSection>
                  <Header className={SECTION_HEADER_CLASS}>Associations</Header>
                  {filteredAssociations.map(opt => (
                    <FieldItem
                      key={`association:${opt.name}`}
                      option={opt}
                      isDisabled={
                        disabledKeys?.has(`${opt.type}:${opt.name}`) ?? false
                      }
                    />
                  ))}
                </ListBoxSection>
              )}
            </ListBox>
          </div>
        </AriaDialog>
      </Popover>
    </DialogTrigger>
  );
}
